/**
 * Admin → Formula Versions (Tier 3A — CJ's formula audit trail, approved by
 * Gigi 2026-07-30).
 *
 * The FORMULA LEDGER rendered as a page:
 *  - Summary grid: every test type, how many calc formulas it has, when a
 *    formula last changed and who applied the change.
 *  - Timeline: the full append-only change history (newest first),
 *    filterable by test type; every change shows the BEFORE and AFTER
 *    formula text — CJ's old-vs-new view.
 *  - Current formulas: with a test type selected, the live registry list
 *    (exactly what tooltips + the Formula Verification PDF print today).
 *
 * Data: GET /api/admin/integrity/formula-versions — served from git-tracked
 * backend/src/calculation/formulas/formulaChangelog.ts. CI enforces that a
 * calc-module edit lands with a ledger entry (checkFormulaChangelog.mjs).
 */

import {
  Alert, Box, Chip, CircularProgress, Collapse, IconButton, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  ExpandLess, ExpandMore, FunctionsOutlined, PersonOutlined, SmartToyOutlined,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import {
  fetchFormulaVersions,
  type FormulaChangeAuthor,
  type FormulaChangeEntry,
  type FormulaVersionsLog,
} from '../../../services/formulaVersionsService';

const AuthorChip = ({ author }: { author: FormulaChangeAuthor }) => {
  const chip = (
    <Chip
      size="small"
      icon={author.kind === 'ai' ? <SmartToyOutlined /> : <PersonOutlined />}
      label={author.name}
      color={author.kind === 'ai' ? 'secondary' : 'default'}
      variant="outlined"
      sx={{ fontWeight: 500 }}
    />
  );
  return author.detail ? <Tooltip title={author.detail}>{chip}</Tooltip> : chip;
};

/** One ledger entry (collapsible; each change shows before → after text). */
const HistoryEntry = ({ entry, defaultOpen }: { entry: FormulaChangeEntry; defaultOpen: boolean }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  const isBaseline = entry.changes.every((c) => c.before === undefined);
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip label={entry.version} color="primary" size="small" sx={{ fontWeight: 700 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{entry.testTypeName}</Typography>
        {isBaseline && <Chip size="small" variant="outlined" label={`BASELINE — ${entry.changes.length} formulas`} />}
        <Typography variant="caption" color="text.secondary">{entry.date}</Typography>
        <AuthorChip author={entry.appliedBy} />
        {entry.requestedBy && (
          <Tooltip title={`Requested by ${entry.requestedBy}`}>
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260 }} noWrap>
              for {entry.requestedBy.replace(/\s*\(.*\)$/, '')}
            </Typography>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={() => setOpen((o) => !o)} aria-label={open ? 'Collapse' : 'Expand'}>
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Stack>
      <Collapse in={open}>
        {entry.context && (
          <Typography variant="caption" sx={{
            display: 'block', mt: 1, px: 1.25, py: 0.75, borderRadius: 1,
            backgroundColor: alpha(theme.palette.info.main, 0.06), color: 'text.secondary',
          }}>
            {entry.context}
          </Typography>
        )}
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          {entry.changes.map((c, i) => (
            <Box key={`${c.columnKey}-${i}`} sx={{
              px: 1.25, py: 0.75, borderRadius: 1,
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
            }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                {c.label}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', ml: 1 }}>
                  {c.columnKey}
                </Typography>
              </Typography>
              {c.before !== undefined && (
                <Typography variant="body2" sx={{
                  fontFamily: 'monospace', fontSize: '0.78rem',
                  color: theme.palette.error.main, textDecoration: 'line-through',
                }}>
                  {c.before}
                </Typography>
              )}
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                {c.after}
              </Typography>
              {c.note && (
                <Typography variant="caption" color="text.secondary">{c.note}</Typography>
              )}
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Paper>
  );
};

export const FormulaVersionsPage = () => {
  const theme = useTheme();
  const [log, setLog] = useState<FormulaVersionsLog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [showCurrent, setShowCurrent] = useState(false);

  useEffect(() => {
    fetchFormulaVersions()
      .then(setLog)
      .catch(() => setError('Failed to load the Formula Ledger.'));
  }, []);

  const history = useMemo(
    () => (log ? log.history.filter((e) => filter === 'all' || e.testTypeId === filter) : []),
    [log, filter],
  );
  const groups = useMemo(() => {
    const byDate: Array<{ date: string; entries: FormulaChangeEntry[] }> = [];
    for (const e of history) {
      const g = byDate[byDate.length - 1];
      if (g && g.date === e.date) g.entries.push(e);
      else byDate.push({ date: e.date, entries: [e] });
    }
    return byDate;
  }, [history]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!log) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  const currentFormulas = filter !== 'all' ? (log.registry[filter] ?? []).filter((f) => f.kind === 'calc' && f.formula) : [];

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <FunctionsOutlined color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Formula Versions</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        The Formula Ledger: every calculation formula in the app, with its full append-only change
        history — who changed which formula, when, why, and the before/after text. A CI gate blocks
        any calculation change that lands without a ledger entry, and the App Integrity suite
        (golden fixtures + mutation proof) provides the validated-after-change evidence.
      </Typography>

      {/* ── Test-type summary grid ────────────────────────────────────────── */}
      <Box sx={{
        display: 'grid', gap: 1.5, mb: 3,
        gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
      }}>
        {log.testTypes.map((t) => (
          <Paper
            key={t.key}
            variant="outlined"
            onClick={() => setFilter(filter === t.key ? 'all' : t.key)}
            sx={{
              p: 1.5, borderRadius: 2, cursor: 'pointer',
              borderColor: filter === t.key ? theme.palette.primary.main : undefined,
              backgroundColor: filter === t.key ? alpha(theme.palette.primary.main, 0.05) : undefined,
              '&:hover': { borderColor: theme.palette.primary.main },
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{t.name}</Typography>
              <Chip label={`${t.formulaCount} ƒ`} color="primary" size="small" sx={{ fontWeight: 700 }} />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              {t.lastAppliedBy && <AuthorChip author={t.lastAppliedBy} />}
              <Typography variant="caption" color="text.secondary">
                {t.lastChanged ?? ''}
              </Typography>
            </Stack>
          </Paper>
        ))}
      </Box>

      {/* ── Current formulas for the selected test type ───────────────────── */}
      {filter !== 'all' && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
              Current formulas — {log.testTypes.find((t) => t.key === filter)?.name}
              {' '}({currentFormulas.length})
            </Typography>
            <IconButton size="small" onClick={() => setShowCurrent((s) => !s)}>
              {showCurrent ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Stack>
          <Collapse in={showCurrent}>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {currentFormulas.map((f) => (
                <Box key={f.columnKey} sx={{ px: 1.25, py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{f.label}</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    {f.formula}
                  </Typography>
                  {f.note && (
                    <Typography variant="caption" color="text.secondary">{f.note}</Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Collapse>
        </Paper>
      )}

      {/* ── Timeline ──────────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Change history</Typography>
        {filter !== 'all' && (
          <Chip
            size="small"
            label={`${log.testTypes.find((t) => t.key === filter)?.name ?? filter} only`}
            onDelete={() => setFilter('all')}
          />
        )}
      </Stack>

      {groups.length === 0 && (
        <Typography variant="body2" color="text.secondary">No entries.</Typography>
      )}
      {groups.map((g) => (
        <Box key={g.date} sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 86 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>{g.date}</Typography>
            <Box sx={{
              width: 10, height: 10, borderRadius: '50%', mt: 0.5,
              backgroundColor: theme.palette.primary.main,
            }} />
            <Box sx={{ flex: 1, width: 2, mt: 0.5, backgroundColor: alpha(theme.palette.primary.main, 0.2) }} />
          </Box>
          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0, pb: 1 }}>
            {g.entries.map((e, i) => (
              <HistoryEntry key={`${e.testTypeId}-${e.version}-${i}`} entry={e} defaultOpen={filter !== 'all' && !e.changes.every((c) => c.before === undefined)} />
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  );
};
